import { AspectRatio, Image, Text, Button, Group } from '@mantine/core'
import classes from './News.module.css'
import {
  IconChevronLeft,
  IconExternalLink,
  IconDownload,
} from '@tabler/icons-react'
import { useLocation, useNavigate, useParams } from 'react-router'
import { useSingleNews } from './api'
import Header from '../common/components/header/Header'
import NewsPlaceholder from '../assets/news_placeholder.png'
import DOMPurify from 'dompurify'
import clsx from 'clsx'
import Meta from '../common/Meta'
import { useMediaQuery } from '@mantine/hooks'
import { Typography } from '../common/typography'
import scroll from '../common/components/scroll/Scroll.module.css'
import { useMarkSeenByEntity, useUnseenContent } from '../notifications/api'
import { useEffect } from 'react'
import { MostRead } from './MostRead'

function getYouTubeEmbedUrl(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
  ]
  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match) {
      return `https://www.youtube.com/embed/${match[1]}`
    }
  }
  return null
}

export default function NewsDetails() {
  const { newsId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const isDesktop = useMediaQuery('(min-width: 800px)')

  const { data: news } = useSingleNews({
    news_id: Number(newsId),
  })

  const { data: unseenContent } = useUnseenContent()
  const { mutate: markSeenByEntity } = useMarkSeenByEntity()

  useEffect(() => {
    const isUnseen = unseenContent?.news_ids?.includes(Number(newsId))
    if (isUnseen) {
      markSeenByEntity({
        entities: [
          {
            entity_id: Number(newsId),
            entity_type: 'news',
          },
        ],
      })
    }
  }, [newsId, unseenContent, markSeenByEntity])

  return (
    <div className={classes.container}>
      <div className={classes.singleNewsHeaderContainer}>
        {isDesktop && (
          <IconChevronLeft
            onClick={() => navigate(location.state?.from?.pathname || '/news')}
            className={classes.backIcon}
          />
        )}
        <Header title="News" back={location.state?.from?.pathname || '/news'} />
      </div>
      <Meta title={`News | ${news.title}`} />
      <div
        className={clsx(classes.newsDetailsContainer, scroll.scroll, {
          [classes.newsDetailsContainerMobile]: !isDesktop,
        })}
      >
        <div className={classes.newsDetailsMedia}>
          {news.external_link && getYouTubeEmbedUrl(news.external_link) ? (
            <AspectRatio ratio={16 / 9}>
              <iframe
                src={getYouTubeEmbedUrl(news.external_link)!}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </AspectRatio>
          ) : (
            <AspectRatio ratio={16 / 9}>
              <Image
                src={news.image.main || NewsPlaceholder}
                alt={news.title}
              />
            </AspectRatio>
          )}
          <div className={classes.newsDetailsCard}>
            <div className={classes.newsDetailsDescription}>
              <Typography level={2} fs="xxxl" fw={700} lh="125">
                {news.title}
              </Typography>
              <Typography
                fs="xs"
                fc={{ light: 'gray-5', dark: 'gray-5' }}
                lh="150"
              >
                {new Date(news.created_at).toLocaleString('en-GB', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                })}
              </Typography>
            </div>
            <div className={classes.newsDetailsBody}>
              <Text
                component="p"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(news.body),
                }}
              />
            </div>

            {news.document && news.document.main && (
              <Group gap="sm" mt="md">
                <Button
                  component="a"
                  href={news.document.main}
                  download
                  leftSection={
                    <IconDownload className={classes.externalButtonIcon} />
                  }
                  variant="light"
                  fullWidth={false}
                  className={classes.externalButton}
                >
                  <Typography
                    fw={500}
                    fc={{ light: 'white', dark: 'white' }}
                    lh="88"
                    tt="uppercase"
                  >
                    Download Document
                  </Typography>
                </Button>

                {news.external_link &&
                  !getYouTubeEmbedUrl(news.external_link) && (
                    <Button
                      component="a"
                      href={news.external_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      leftSection={
                        <IconExternalLink
                          className={classes.externalButtonIcon}
                        />
                      }
                      variant="light"
                      fullWidth={false}
                      className={classes.externalButton}
                    >
                      <Typography
                        fw={500}
                        fc={{ light: 'white', dark: 'white' }}
                        lh="88"
                        tt="uppercase"
                      >
                        View External Resource
                      </Typography>
                    </Button>
                  )}
              </Group>
            )}
          </div>
        </div>
        {isDesktop && (
          <MostRead title="Discover More" news_id={Number(newsId)} />
        )}
      </div>
    </div>
  )
}
